<template>
  <div class="cv-page">
    <link
      rel="stylesheet"
      href="https://cdnjs.cloudflare.com/ajax/libs/github-markdown-css/5.5.1/github-markdown.min.css"
    />

    <!-- CV Header Navigation -->
    <header class="cv-nav-header">
      <div class="cv-nav-container">
        <router-link to="/" class="back-link">
          <span class="arrow">←</span>
          <span>Back to Posts</span>
        </router-link>

        <div class="cv-actions">
          <button @click="downloadCv" class="cv-btn primary-btn" :disabled="isLoading || !cvContent">
            <span>Download .md</span>
          </button>
          <button @click="printCv" class="cv-btn secondary-btn" :disabled="isLoading || !cvContent">
            <span>Print / PDF</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Main CV Content Area -->
    <main class="cv-main-container">
      <div v-if="isLoading" class="cv-loading">
        <div class="spinner"></div>
        <p>Loading Curriculum Vitae...</p>
      </div>

      <div v-else-if="cvContent" class="cv-card">
        <Markdown :source="cvContent" class="markdown-body" />
      </div>

      <div v-else class="cv-empty">
        <p>CV content is currently unavailable.</p>
        <router-link to="/" class="back-btn">Return to Home</router-link>
      </div>
    </main>

    <!-- Footer -->
    <FooterSection />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Markdown from 'vue3-markdown-it';
import contentService from '../services/contentService';
import FooterSection from '../components/FooterSection.vue';

const cvContent = ref('');
const isLoading = ref(true);

onMounted(async () => {
  try {
    cvContent.value = await contentService.getCv();
  } catch (err) {
    console.error('Failed to load CV:', err);
  } finally {
    isLoading.value = false;
  }
});

const downloadCv = () => {
  if (!cvContent.value) return;
  const blob = new Blob([cvContent.value], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'David_Van_Der_Merwe_CV.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const printCv = () => {
  window.print();
};
</script>

<style scoped>
.cv-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-dark);
}

.cv-nav-header {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(12px);
  background: rgba(40, 44, 52, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 16px 20px;
}

.cv-nav-container {
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #e5e7eb;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  transition: all 0.2s ease;
}

.back-link:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  transform: translateX(-2px);
}

.arrow {
  font-size: 1.1rem;
}

.cv-actions {
  display: flex;
  gap: 10px;
}

.cv-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.primary-btn {
  background-color: var(--accent-blue);
  color: #ffffff;
}

.primary-btn:hover:not(:disabled) {
  background-color: var(--accent-blue-hover);
  transform: translateY(-1px);
}

.secondary-btn {
  background-color: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.secondary-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}

.cv-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cv-main-container {
  max-width: 960px;
  width: 100%;
  margin: 30px auto 60px;
  padding: 0 20px;
  flex-grow: 1;
}

.cv-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.markdown-body {
  color: #24292f;
  background-color: transparent !important;
  font-family: var(--font-family);
  font-size: 1rem;
  line-height: 1.7;
}

.markdown-body :deep(h1) {
  font-size: 2.2rem;
  color: #111827;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 12px;
  margin-top: 0;
  margin-bottom: 16px;
}

.markdown-body :deep(h2) {
  font-size: 1.5rem;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 8px;
  margin-top: 32px;
  margin-bottom: 16px;
}

.markdown-body :deep(h3) {
  font-size: 1.2rem;
  color: #374151;
  margin-top: 24px;
  margin-bottom: 8px;
}

.markdown-body :deep(a) {
  color: var(--accent-blue);
  text-decoration: none;
}

.markdown-body :deep(a:hover) {
  text-decoration: underline;
}

.markdown-body :deep(ul) {
  padding-left: 24px;
  margin-bottom: 16px;
}

.markdown-body :deep(li) {
  margin-bottom: 6px;
}

.markdown-body :deep(hr) {
  margin: 28px 0;
  border: 0;
  border-top: 1px solid #e5e7eb;
}

.cv-loading,
.cv-empty {
  text-align: center;
  padding: 80px 20px;
  color: var(--text-white-dim);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--accent-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .cv-card {
    padding: 24px 20px;
    border-radius: 12px;
  }
  .cv-nav-header {
    padding: 12px 16px;
  }
  .cv-btn {
    padding: 6px 12px;
    font-size: 0.82rem;
  }
}

@media print {
  .cv-nav-header,
  .site-footer {
    display: none !important;
  }
  .cv-page {
    background: #ffffff !important;
  }
  .cv-card {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
  }
}
</style>
